import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Settings, Cookie } from 'lucide-react';
import { cookieService, type CookieConsent } from '../utils/cookieService';
import { useAuthStore } from '../store/authStore';
import { clsx } from 'clsx';

export function CookieConsentBanner() {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookieConsent>({
    essential: true, // Always true
    functional: false,
    analytics: false,
    marketing: false,
    lastUpdated: new Date()
  });

  const hasCheckedConsent = useRef(false);
  const language = user?.preferredLanguage?.toLowerCase() || 'fr';

  useEffect(() => {
    // Only check consent once when component mounts
    if (!hasCheckedConsent.current) {
      const savedConsent = cookieService.getConsent();
      if (!savedConsent) {
        setIsOpen(true);
      } else {
        setPreferences(savedConsent);
      }
      hasCheckedConsent.current = true;
    }
  }, []);

  const handleAcceptAll = () => {
    const newConsent = cookieService.setConsent({
      functional: true,
      analytics: true,
      marketing: true
    });
    setPreferences(newConsent);
    setIsOpen(false);
  };

  const handleAcceptSelected = () => {
    const newConsent = cookieService.setConsent(preferences);
    setPreferences(newConsent);
    setIsOpen(false);
    setShowDetails(false);
  };

  const handleTogglePreference = (category: keyof CookieConsent) => {
    if (category === 'essential' || category === 'lastUpdated') return;
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 inset-x-0 pb-2 sm:pb-5 z-50"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="p-4 sm:p-5 rounded-2xl bg-white/95 backdrop-blur-xl shadow-elevated border border-slate-200/60">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 flex items-start sm:items-center gap-3">
                <div className="p-2 rounded-xl bg-primary-50 flex-shrink-0">
                  <Cookie className="h-5 w-5 text-primary-600" />
                </div>
                <p className="text-sm font-medium text-slate-700 leading-relaxed">
                  {language === 'fr' ? (
                    "Nous utilisons des cookies pour ameliorer votre experience"
                  ) : language === 'en' ? (
                    "We use cookies to enhance your experience"
                  ) : (
                    "Utilizziamo i cookie per migliorare la tua esperienza"
                  )}
                </p>
              </div>

              <div className="flex gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all active:scale-[0.98]"
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {language === 'fr' ? "Personnaliser" : language === 'en' ? "Customize" : "Personalizza"}
                  </span>
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 sm:flex-initial flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg shadow-primary-600/20 transition-all active:scale-[0.98]"
                >
                  {language === 'fr' ? "Tout accepter" : language === 'en' ? "Accept all" : "Accetta tutto"}
                </button>
              </div>
            </div>

            {showDetails && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="space-y-3">
                  {Object.entries(preferences).map(([key, value]) => {
                    if (key === 'lastUpdated') return null;
                    const isEssential = key === 'essential';
                    return (
                      <div key={key} className="flex items-center justify-between gap-4 p-3 rounded-xl bg-slate-50/80">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800">
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {isEssential ? (language === 'fr' ? "Requis pour le fonctionnement du site" : language === 'en' ? "Required for site operation" : "Necessario per il funzionamento del sito") : `Cookies ${key}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleTogglePreference(key as keyof CookieConsent)}
                          disabled={isEssential}
                          className={clsx(
                            'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer',
                            'transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                            value ? 'bg-primary-600' : 'bg-slate-300',
                            isEssential && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          <span
                            className={clsx(
                              'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow-sm transform ring-0 transition ease-in-out duration-200',
                              value ? 'translate-x-5' : 'translate-x-0'
                            )}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleAcceptSelected}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg shadow-primary-600/20 transition-all active:scale-[0.98]"
                  >
                    {language === 'fr' ? "Enregistrer mes choix" : language === 'en' ? "Save my choices" : "Salva le mie scelte"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}